import { CredentialResponse, GoogleLogin } from '@react-oauth/google';
import { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Props for GoogleLoginButton component
 */
interface GoogleLoginButtonProps {
	/** Additional CSS classes */
	className?: string;
	/** Custom button text */
	text?: string;
	/** Callback for successful login */
	onSuccess?: () => void;
	/** Callback for login error */
	onError?: (error: string) => void;
}

/**
 * Google OAuth login button component
 * p
 * @description Provides Google OAuth authentication using the @react-oauth/google library.
 * Handles the complete OAuth flow and integrates with the backend authentication API.
 *
 * Features:
 * - Google OAuth popup/redirect flow
 * - Loading states and error handling
 * - Integration with AuthContext
 * - Responsive design with Tailwind CSS
 * - Accessibility features
 *
 * @example
 * ```tsx
 * function LoginPage() {
 *   return (
 *     <div className="flex justify-center">
 *       <GoogleLoginButton
 *         onSuccess={() => navigate('/dashboard')}
 *         onError={(error) => showError(error)}
 *       />
 *     </div>
 *   );
 * }
 * ```
 */
export function GoogleLoginButton({
	className = '',
	text = 'Sign in with Google',
	onSuccess,
	onError,
}: GoogleLoginButtonProps) {
	const { login } = useAuth();
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	/**
	 * Handle successful Google OAuth response
	 */
	const handleGoogleSuccess = async (response: CredentialResponse) => {
		setIsLoading(true);
		setError(null);

		try {
			// The response contains the ID token we need for backend authentication
			const idToken = response.credential;

			if (!idToken) {
				throw new Error('No ID token received from Google');
			}

			// Authenticate with backend using the ID token
			await login(idToken);

			// Call success callback
			onSuccess?.();
		} catch (err: any) {
			const errorMessage = err.message || 'Authentication failed';
			setError(errorMessage);
			onError?.(errorMessage);
			console.error('Google login error:', err);
		} finally {
			setIsLoading(false);
		}
	};

	/**
	 * Handle Google OAuth error
	 */
	const handleGoogleError = () => {
		const errorMessage = 'Google authentication failed';
		setError(errorMessage);
		onError?.(errorMessage);
		setIsLoading(false);
	};

	return (
		<div className={`relative space-y-4 ${className}`}>
			{/* Error message */}
			{error && (
				<div className="p-4 text-red-700 bg-red-50 rounded-lg border border-red-200 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">
					<div className="flex items-center">
						<svg className="mr-2 w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
							<path
								fillRule="evenodd"
								d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
								clipRule="evenodd"
							/>
						</svg>
						{error}
					</div>
				</div>
			)}

			{/* Google login component */}
			<div className="w-full">
				<GoogleLogin
					onSuccess={handleGoogleSuccess}
					onError={handleGoogleError}
					useOneTap={false}
					theme="outline"
					size="large"
					text="signin_with"
					shape="rectangular"
					width="100%"
				/>
			</div>

			{/* Loading state overlay */}
			{isLoading && (
				<div className="flex absolute inset-0 justify-center items-center bg-white bg-opacity-75 rounded-lg dark:bg-gray-800 dark:bg-opacity-75">
					<div className="flex items-center space-x-2">
						<svg
							className="w-5 h-5 text-blue-600 animate-spin"
							xmlns="http://www.w3.org/2000/svg"
							fill="none"
							viewBox="0 0 24 24"
						>
							<circle
								className="opacity-25"
								cx="12"
								cy="12"
								r="10"
								stroke="currentColor"
								strokeWidth="4"
							></circle>
							<path
								className="opacity-75"
								fill="currentColor"
								d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
							></path>
						</svg>
						<span className="text-sm text-gray-600 dark:text-gray-400">
							Signing you in...
						</span>
					</div>
				</div>
			)}
		</div>
	);
}
